import { useState, useEffect } from 'react';
import { useModalStore } from '../../store/modalStore';
import { useToastStore } from '../../store/toastStore';
import { useAuthStore } from '../../store/authStore';
import type { Book } from '../../api/books';

interface BookUpdatePayload {
  title?: string;
  authors?: string[];
  publisher?: string;
  published_date?: string;
  language?: string;
  identifier?: string;
  description?: string;
  enrichment?: EnrichmentPayload;
}

interface EnrichmentPayload {
  genre?: string;
  series_name?: string;
  series_position?: number;
  subjects?: string[];
}

export function EditBookModal() {
  const { isOpen, modalType, modalData, closeModal } = useModalStore();
  const addToast = useToastStore((state) => state.addToast);
  const accessToken = useAuthStore((state) => state.accessToken);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [authors, setAuthors] = useState('');
  const [publisher, setPublisher] = useState('');
  const [publishedDate, setPublishedDate] = useState('');
  const [language, setLanguage] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [description, setDescription] = useState('');
  const [genre, setGenre] = useState('');
  const [seriesName, setSeriesName] = useState('');
  const [seriesPosition, setSeriesPosition] = useState('');
  const [subjects, setSubjects] = useState('');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [hasOriginalCover, setHasOriginalCover] = useState(false);
  const [showQualityWarning, setShowQualityWarning] = useState(false);
  const [qualityWarningDetails, setQualityWarningDetails] = useState<{
    message: string;
    current_width: number;
    current_height: number;
    min_width: number;
    min_height: number;
  } | null>(null);

  const book = modalData as Book;

  useEffect(() => {
    if (isOpen && modalType === 'editBook' && book) {
      setTitle(book.title || '');
      setAuthors((book.authors?.map(a => a.name) || []).join(', '));
      setPublisher(book.publisher || '');
      setPublishedDate(book.publishedDate || '');
      setLanguage(book.language || '');
      setIdentifier(book.identifier || '');
      setDescription(book.description || '');
      setGenre(book.enrichment?.genre || '');
      setSeriesName(book.enrichment?.series_name || '');
      setSeriesPosition(book.enrichment?.series_position?.toString() || '');
      setSubjects((book.enrichment?.subjects || []).join(', '));
      setHasOriginalCover(!!book.originalCoverPath);
      setCoverFile(null);
      setError(null);
    }
  }, [isOpen, modalType, book]);

  if (!isOpen || modalType !== 'editBook' || !book) return null;

  const handleRestoreCover = async () => {
    if (!hasOriginalCover) return;

    try {
      const headers: Record<string, string> = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`/api/books/${book.id}/cover/restore`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to restore cover');
      }

      addToast('Cover restored successfully', 'success');
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleUploadCover = async (override = false) => {
    if (!coverFile) return true;

    const formData = new FormData();
    formData.append('cover', coverFile);
    if (override) {
      formData.append('override', 'true');
    }

    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const coverResponse = await fetch(`/api/books/${book.id}/cover`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    });

    if (!coverResponse.ok) {
      const err = await coverResponse.json();
      if (err.error === 'low_quality_image' && err.can_override) {
        setQualityWarningDetails(err);
        setShowQualityWarning(true);
        return false;
      }
      throw new Error(err.error || err.message || 'Failed to upload cover');
    }

    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const uploadSuccess = await handleUploadCover(false);
      if (!uploadSuccess) {
        setSaving(false);
        return;
      }

      const payload: BookUpdatePayload = {};
      if (title) payload.title = title;
      if (authors) payload.authors = authors.split(',').map((a) => a.trim()).filter((a) => a);
      if (publisher) payload.publisher = publisher;
      if (publishedDate) payload.published_date = publishedDate;
      if (language) payload.language = language;
      if (identifier) payload.identifier = identifier;
      if (description) payload.description = description;

      const enrichmentPayload: EnrichmentPayload = {};
      if (genre) enrichmentPayload.genre = genre;
      if (seriesName) enrichmentPayload.series_name = seriesName;
      if (seriesPosition) enrichmentPayload.series_position = parseFloat(seriesPosition);
      if (subjects) enrichmentPayload.subjects = subjects.split(',').map((s) => s.trim()).filter((s) => s);

      if (Object.keys(enrichmentPayload).length > 0) {
        payload.enrichment = enrichmentPayload;
      }

      const patchHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      if (accessToken) {
        patchHeaders['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`/api/books/${book.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: patchHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update book');
      }

      addToast('Book updated successfully', 'success');
      closeModal();
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Book</h2>
          <button onClick={closeModal} className="btn-close" type="button">
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="authors">Authors (comma-separated)</label>
            <input
              id="authors"
              type="text"
              value={authors}
              onChange={(e) => setAuthors(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="publisher">Publisher</label>
            <input
              id="publisher"
              type="text"
              value={publisher}
              onChange={(e) => setPublisher(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="publishedDate">Published Date</label>
            <input
              id="publishedDate"
              type="text"
              value={publishedDate}
              onChange={(e) => setPublishedDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="language">Language</label>
            <input
              id="language"
              type="text"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="identifier">Identifier (ISBN)</label>
            <input
              id="identifier"
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>

          <div className="form-group">
            <label htmlFor="genre">Genre</label>
            <input
              id="genre"
              type="text"
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="seriesName">Series Name</label>
            <input
              id="seriesName"
              type="text"
              value={seriesName}
              onChange={(e) => setSeriesName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="seriesPosition">Series Position</label>
            <input
              id="seriesPosition"
              type="text"
              value={seriesPosition}
              onChange={(e) => setSeriesPosition(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="subjects">Subjects (comma-separated)</label>
            <input
              id="subjects"
              type="text"
              value={subjects}
              onChange={(e) => setSubjects(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="cover">Cover Image</label>
            <input
              id="cover"
              type="file"
              accept="image/*"
              onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
            />
            {hasOriginalCover && (
              <button
                onClick={handleRestoreCover}
                className="btn btn-secondary btn-sm"
                type="button"
              >
                Restore Original Cover
              </button>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={closeModal} className="btn btn-secondary" type="button">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="btn btn-primary"
            disabled={saving}
            type="button"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {showQualityWarning && qualityWarningDetails && (
        <div className="modal-overlay" onClick={() => setShowQualityWarning(false)}>
          <div className="modal-content quality-warning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Low Image Quality Warning</h2>
              <button onClick={() => setShowQualityWarning(false)} className="btn-close" type="button">
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="warning-message">
                <p><strong>{qualityWarningDetails.message}</strong></p>
                <div className="quality-details">
                  <p>Current resolution: <strong>{qualityWarningDetails.current_width} × {qualityWarningDetails.current_height}px</strong></p>
                  <p>Recommended minimum: <strong>{qualityWarningDetails.min_width} × {qualityWarningDetails.min_height}px</strong></p>
                </div>
                <p className="quality-explanation">
                  Low-resolution images may appear pixelated or blurry when displayed.
                  For the best quality, we recommend using higher-resolution images.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowQualityWarning(false)}
                className="btn btn-secondary"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowQualityWarning(false);
                  setSaving(true);
                  try {
                    await handleUploadCover(true);

                    const payload: BookUpdatePayload = {};
                    if (title) payload.title = title;
                    if (authors) payload.authors = authors.split(',').map((a) => a.trim()).filter((a) => a);
                    if (publisher) payload.publisher = publisher;
                    if (publishedDate) payload.published_date = publishedDate;
                    if (language) payload.language = language;
                    if (identifier) payload.identifier = identifier;
                    if (description) payload.description = description;

                    const enrichmentPayload: EnrichmentPayload = {};
                    if (genre) enrichmentPayload.genre = genre;
                    if (seriesName) enrichmentPayload.series_name = seriesName;
                    if (seriesPosition) enrichmentPayload.series_position = parseFloat(seriesPosition);
                    if (subjects) enrichmentPayload.subjects = subjects.split(',').map((s) => s.trim()).filter((s) => s);

                    if (Object.keys(enrichmentPayload).length > 0) {
                      payload.enrichment = enrichmentPayload;
                    }

                    const overrideHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
                    if (accessToken) {
                      overrideHeaders['Authorization'] = `Bearer ${accessToken}`;
                    }

                    const response = await fetch(`/api/books/${book.id}`, {
                      method: 'PATCH',
                      credentials: 'include',
                      headers: overrideHeaders,
                      body: JSON.stringify(payload),
                    });

                    if (!response.ok) {
                      throw new Error('Failed to update book');
                    }

                    addToast('Book updated successfully', 'success');
                    closeModal();
                    setTimeout(() => window.location.reload(), 500);
                  } catch (err) {
                    setError((err as Error).message);
                  } finally {
                    setSaving(false);
                  }
                }}
                className="btn btn-warning"
                type="button"
              >
                Upload Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
